/**
 * Translations Service
 *
 * Manages translations data for the Vue application.
 * Fetches translations from the languages API in a vue-i18n-compatible format.
 */

import { reactive } from 'vue'
import { log } from '../utils/DebugLogger'
import { config } from './configService'
import { cacheService } from './cache'

const autoInsertMissingTranslations = true

export type Locale = string
export type TranslationsMap = Record<string, string>

export interface TranslationsState {
  messages: Record<Locale, TranslationsMap>
  isLoading: boolean
  lastError: Error | null
  cacheExpiry: Record<string, number>
}

export interface TranslationsStats {
  locales: number
  totalTranslations: number
  cacheEntries: number
  isLoading: boolean
  lastError: Error | null
}

export const translationsState: TranslationsState = reactive({
  messages: {},
  isLoading: false,
  lastError: null,
  cacheExpiry: {}
})

interface ApiTranslationItem {
  default_val?: string
  val?: string
  [key: string]: unknown
}

class TranslationsService {
  baseUrl: string
  cacheDuration: number
  supportedLocales: string[]
  fallbackLocale: string
  // Keys already requested for auto-registration this session. vue-i18n's `missing`
  // handler fires on EVERY render of a missing key, so without this guard the same
  // line is POSTed dozens of times concurrently — a "registration storm" that races
  // translations-service into 500s. Register each (page,locale,key) at most once.
  private requestedMissingKeys = new Set<string>()

  constructor() {
    this.baseUrl = `${config.api.idiomasHost}/`
    this.cacheDuration = 4 * 60 * 60 * 1000
    this.supportedLocales = ['pt', 'es', 'en', 'fr']
    this.fallbackLocale = 'pt'
  }

  async initialize(locales: Locale[] = ['pt-BR'], pages: string[] = ['global']): Promise<Record<Locale, TranslationsMap>> {
    log('TranslationsService', 'initialize', 'Initializing translations', { locales, pages })

    translationsState.isLoading = true
    translationsState.lastError = null

    try {
      const messages: Record<Locale, TranslationsMap> = {}

      for (const locale of locales) {
        messages[locale] = {}

        for (const page of pages) {
          const pageTranslations = await this.getTranslationsForPage(page, locale)
          log('TranslationsService', 'initialize', 'Page translations', { page, locale, pageTranslations })
          Object.assign(messages[locale], pageTranslations)
        }

        translationsState.messages[locale] = messages[locale]
        log('TranslationsService', 'initialize', 'Storing translations in reactive state', { locale, messages: messages[locale] })
        translationsState.cacheExpiry[locale] = Date.now() + this.cacheDuration
      }

      log('TranslationsService', 'initialize', 'Translations initialized successfully', {
        locales: Object.keys(messages),
        totalKeys: Object.values(messages).reduce((total, msgs) => total + Object.keys(msgs).length, 0)
      })

      return messages

    } catch (error) {
      translationsState.lastError = error as Error
      console.error('[TranslationsService][initialize] Error initializing translations:', error)
      throw error
    } finally {
      translationsState.isLoading = false
    }
  }

  async getTranslationsForPage(pageName: string, locale: Locale = this.fallbackLocale): Promise<TranslationsMap> {
    const cacheKey = `translations.v2.${pageName}_${locale}` // v2: busts caches holding pre-sanitization values (raw '@' crashes the i18n compiler)

    log('TranslationsService', 'getTranslationsForPage', 'Fetching translations', {
      pageName,
      locale,
      cacheKey
    })

    try {
      const translations = await cacheService.get<TranslationsMap>(cacheKey, async () => {
        log('TranslationsService', 'getTranslationsForPage', 'Cache miss, fetching from API')

        const url = `clients/translations/listDefaultValues/Frontend/${locale}`
        const response = await this.makeApiRequest<{ success?: boolean; data?: ApiTranslationItem[] | TranslationsMap }>(url)

        if (response && response.success && response.data) {
          const dataLength = Array.isArray(response.data) ? response.data.length : Object.keys(response.data).length
          log('TranslationsService', 'getTranslationsForPage', 'Translations fetched successfully', {
            page: pageName,
            locale,
            count: dataLength
          })

          const processed = this.processTranslationsData(response.data)
          log('TranslationsService', 'getTranslationsForPage', 'Translations processed successfully', {
            count: Object.keys(processed).length
          })

          return processed
        }

        log('TranslationsService', 'getTranslationsForPage', 'No translations found', { pageName, locale })
        return {}
      })

      if (translations && Object.keys(translations).length > 0) {
        translationsState.messages[locale] = {
          ...translationsState.messages[locale],
          ...translations
        }
        translationsState.cacheExpiry[cacheKey] = Date.now() + this.cacheDuration
      }

      return translations || {}

    } catch (error) {
      console.error(`[TranslationsService][getTranslationsForPage] Error fetching translations for ${pageName}/${locale}:`, error)

      const fallbackTranslations = this.getFallbackTranslations(pageName, locale)
      if (fallbackTranslations) {
        log('TranslationsService', 'getTranslationsForPage', 'Using fallback translations')
        return fallbackTranslations
      }

      return {}
    }
  }

  getTranslationsForLocale(locale: Locale): TranslationsMap {
    return translationsState.messages[locale] || {}
  }

  getAvailableLocales(): Locale[] {
    return Object.keys(translationsState.messages)
  }

  /** @deprecated Use CacheService's TTL mechanism instead */
  isCacheValid(cacheKey: string): boolean {
    const expiry = translationsState.cacheExpiry[cacheKey]
    return !!expiry && Date.now() < expiry
  }

  /** @deprecated CacheService now handles caching */
  getCachedTranslations(cacheKey: string): TranslationsMap | null {
    const parts = cacheKey.split('_')
    const locale = parts[parts.length - 1]
    return translationsState.messages[locale] || null
  }

  /** @deprecated CacheService now handles caching */
  async cacheTranslations(cacheKey: string, translations: TranslationsMap): Promise<void> {
    try {
      const fullKey = `translations.${cacheKey}`
      await cacheService.set(fullKey, translations)
      translationsState.cacheExpiry[cacheKey] = Date.now() + this.cacheDuration
      log('TranslationsService', 'cacheTranslations', 'Cached via CacheService', { cacheKey })
    } catch (error) {
      log('TranslationsService', 'cacheTranslations', 'Error caching translations', error)
    }
  }

  async clearCache(): Promise<void> {
    try {
      await cacheService.invalidatePattern('translations.*')

      translationsState.messages = {}
      translationsState.cacheExpiry = {}

      log('TranslationsService', 'clearCache', 'Translation cache cleared via CacheService')
    } catch (error) {
      console.error('[TranslationsService][clearCache] Error clearing cache:', error)
    }
  }

  /**
   * Escape characters vue-i18n's message compiler treats as syntax.
   * A raw '@' in a stored translation (e.g. "seu@email.com") parses as a
   * linked-message reference and throws SyntaxError 10 (INVALID_LINKED_FORMAT)
   * mid-render, blanking the whole page. Escape '@' to the literal {'@'}
   * unless it starts a real linked reference (@: / @.modifier:) or is
   * already escaped (idempotent).
   */
  sanitizeMessage(value: string): string {
    return value.replace(/(?<!\{')@(?![.:]|'\})/g, "{'@'}")
  }

  processTranslationsData(data: ApiTranslationItem[] | TranslationsMap | unknown): TranslationsMap {
    if (Array.isArray(data)) {
      const translations: TranslationsMap = {}
      log('TranslationsService', 'processTranslationsData', 'Processing translations data', { data })
      data.forEach(item => {
        if (item.default_val && item.val) {
          translations[item.default_val] = this.sanitizeMessage(item.val)
        }
      })
      return translations
    } else if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      log('TranslationsService', 'processTranslationsData', 'Processing translations data', { data })
      const sanitized: TranslationsMap = {}
      for (const [key, value] of Object.entries(data as TranslationsMap)) {
        sanitized[key] = typeof value === 'string' ? this.sanitizeMessage(value) : value
      }
      return sanitized
    }

    log('TranslationsService', 'processTranslationsData', 'Translations processed successfully', { data })
    return {}
  }

  getFallbackTranslations(pageName: string, locale: Locale): TranslationsMap | null {
    if (locale !== this.fallbackLocale) {
      const fallbackKey = `${pageName}_${this.fallbackLocale}`
      return this.getCachedTranslations(fallbackKey)
    }
    return null
  }

  async makeApiRequest<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(options.headers as Record<string, string>)
        },
        ...options
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      log('TranslationsService', 'makeApiRequest', 'API request successful', {
        url,
        status: response.status,
        dataType: typeof data
      })

      return data as T

    } catch (error) {
      log('TranslationsService', 'makeApiRequest', 'API request failed', { url, error: (error as Error).message })
      throw error
    }
  }

  async addMissingTranslation(pageName: string, shortcode: string, defaultValue: string, locale: Locale = this.fallbackLocale): Promise<boolean> {
    if (!autoInsertMissingTranslations) return false

    // Dedup: only request creation of a given line ONCE per session. The guard is
    // synchronous (set membership added before the await), so even a burst of
    // concurrent calls for the same key — vue-i18n re-invokes `missing` on every
    // render — collapses to a single POST, eliminating the storm + its 500 race.
    const dedupKey = `${pageName}::${locale}::${shortcode}`
    if (this.requestedMissingKeys.has(dedupKey)) return false
    this.requestedMissingKeys.add(dedupKey)

    try {
      log('TranslationsService', 'addMissingTranslation', 'Adding missing translation', {
        pageName, shortcode, defaultValue, locale
      })

      const requestData = {
        default_val: defaultValue,
        page_name: pageName,
        name: defaultValue
      }

      await this.makeApiRequest('clients/lines/saveByPageName', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      })

      const cacheKey = `translations.v2.${pageName}_${locale}` // v2: busts caches holding pre-sanitization values (raw '@' crashes the i18n compiler)
      await cacheService.invalidate(cacheKey)
      delete translationsState.cacheExpiry[`${pageName}_${locale}`]

      log('TranslationsService', 'addMissingTranslation', 'Translation added and cache invalidated', { cacheKey })
      return true

    } catch (error) {
      // Release the dedup slot so a genuinely-failed registration can be retried on
      // a later render. Still storm-safe: the slot is held for the whole in-flight
      // request, so at most one attempt per key runs at a time (concurrent renders
      // during the await are deduped); only after a failure does another become possible.
      this.requestedMissingKeys.delete(dedupKey)
      log('TranslationsService', 'addMissingTranslation', 'Error (non-fatal, swallowed)', error)
      return false
    }
  }

  async preloadCommonTranslations(locale: Locale, pages: string[] = ['global', 'common', 'errors', 'forms']): Promise<void> {
    log('TranslationsService', 'preloadCommonTranslations', 'Preloading translations', { locale, pages })

    try {
      const promises = pages.map(page =>
        this.getTranslationsForPage(page, locale).catch(error => {
          log('TranslationsService', 'preloadCommonTranslations', `Failed to preload ${page}`, error)
          return {} as TranslationsMap
        })
      )

      await Promise.all(promises)
      log('TranslationsService', 'preloadCommonTranslations', 'Preloading completed')
    } catch (error) {
      console.error('[TranslationsService][preloadCommonTranslations] Error preloading translations:', error)
    }
  }

  getStats(): TranslationsStats {
    const stats: TranslationsStats = {
      locales: Object.keys(translationsState.messages).length,
      totalTranslations: 0,
      cacheEntries: Object.keys(translationsState.cacheExpiry).length,
      isLoading: translationsState.isLoading,
      lastError: translationsState.lastError
    }

    Object.values(translationsState.messages).forEach(messages => {
      stats.totalTranslations += Object.keys(messages).length
    })

    return stats
  }

  async destroy(): Promise<void> {
    await this.clearCache()
    translationsState.isLoading = false
    translationsState.lastError = null
    log('TranslationsService', 'destroy', 'Service destroyed and state cleared')
  }
}

export const translationsService = new TranslationsService()
export { TranslationsService }
export default translationsService
